import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { accessControlAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { PATH_TO_MODULE, pagesOf } from '../../components/Sidebar/menuConfig';
import './AccessControl.css';

/*
 * Access Control (superadmin): the roles — the built-in ones and those the superadmin
 * makes — what each may do, and which sidebar pages each sees; then, administrator by
 * administrator, the role they work as and pages hidden or always shown just for them.
 */

const BASE_ROLES = [
    { key: 'tenant_admin', label: 'Tenant Admin', icon: 'fa-building',     scope: 'their organization and all of its branches' },
    { key: 'branch_admin', label: 'Branch Admin', icon: 'fa-code-branch',  scope: 'their one branch' },
    { key: 'support',      label: 'Support',      icon: 'fa-headset',      scope: 'the organization or branch set on them' },
    { key: 'viewer',       label: 'Viewer',       icon: 'fa-eye',          scope: 'the organization or branch set on them' },
];
const BASE = Object.fromEntries(BASE_ROLES.map((r) => [r.key, r]));

const ACTIONS = [
    { key: 'view',      label: 'View',      hint: 'Open the pages and read their data' },
    { key: 'create',    label: 'Create',    hint: 'Add new records' },
    { key: 'edit',      label: 'Edit',      hint: 'Change records' },
    { key: 'delete',    label: 'Delete',    hint: 'Remove records' },
    { key: 'export',    label: 'Export',    hint: 'Download / export' },
    { key: 'configure', label: 'Configure', hint: 'Settings and actions on live systems' },
];
const CRUD = ['view', 'create', 'edit', 'delete'];

// What a role may do, module by module: the pages each module's permission governs, and
// only the actions something checks (the API, or the page hiding its buttons) — a box
// that changes nothing is not shown. Modules not listed are saved as they are.
const MODULE_GROUPS = [
    {
        label: 'Organization',
        modules: [
            { key: 'dashboard', label: 'Dashboard', icon: 'fa-tachometer-alt', covers: 'Dashboard figures, Interface Traffic', actions: ['view'] },
            { key: 'branches',  label: 'Branches', icon: 'fa-code-branch', covers: 'Branches (My Branch)', actions: CRUD },
            { key: 'admins',    label: 'Administrators', icon: 'fa-user-shield', covers: 'Administrators (Staff)', actions: CRUD },
            { key: 'users',     label: 'Guest Users', icon: 'fa-users', covers: 'Guest Wi-Fi accounts', actions: CRUD },
            { key: 'settings',  label: 'Rooms, User Types & VLANs', icon: 'fa-hotel', covers: 'Room Management, Branch Pages, User Types, VLAN Profiles · Configure: rooms, check-in / out, import, PMS sync', actions: [...CRUD, 'configure'] },
        ],
    },
    {
        label: 'Network',
        modules: [
            { key: 'routers', label: 'NAS Devices', icon: 'fa-server', covers: 'NAS Devices · Configure: the router setup buttons, a gateway\'s Dynamic Bandwidth Manager', actions: [...CRUD, 'configure'] },
            { key: 'hotspot', label: 'Router Setup', icon: 'fa-broadcast-tower', covers: 'Push config, SSL, live users and kick (MikroTik)', actions: ['view', 'configure'] },
            { key: 'devices', label: 'Devices', icon: 'fa-laptop', covers: 'Connected Devices, Device Status', actions: CRUD },
        ],
    },
    {
        label: 'Internet & sessions',
        modules: [
            { key: 'plans',     label: 'Internet Plans', icon: 'fa-tags', covers: 'Plans, Add-Ons, Membership Tiers, Plan Templates, Policy Engine, Plan Scheduler', actions: CRUD },
            { key: 'vouchers',  label: 'Vouchers', icon: 'fa-ticket-alt', covers: 'Vouchers · Export: the export button', actions: [...CRUD, 'export'] },
            { key: 'sessions',  label: 'Sessions', icon: 'fa-wifi', covers: 'Active Sessions, Session Dashboard, Connected Users, Internet Plans → Live Bandwidth, Device Authorization, Upgrade Approvals · Edit: a guest\'s speed, live · Delete: disconnect', actions: ['view', 'edit', 'delete'] },
            { key: 'auth_sets', label: 'Authorization Sets', icon: 'fa-shield-alt', covers: 'Authorization Sets', actions: CRUD },
        ],
    },
    {
        label: 'Guest pages & marketing',
        modules: [
            { key: 'templates',     label: 'Splash Pages', icon: 'fa-paint-brush', covers: 'Splash Pages', actions: CRUD },
            { key: 'landing_pages', label: 'Landing Page Builder', icon: 'fa-pencil-ruler', covers: 'Pages built in the builder · Configure: publish, make default', actions: [...CRUD, 'configure'] },
            { key: 'media',         label: 'Media Library', icon: 'fa-photo-video', covers: 'Logos and images', actions: CRUD },
            { key: 'campaigns',     label: 'Marketing & Leads', icon: 'fa-bullhorn', covers: 'Campaigns and leads', actions: CRUD },
        ],
    },
    {
        label: 'Integrations, reports & logs',
        modules: [
            { key: 'pms',        label: 'PMS & Authorization Logs', icon: 'fa-plug', covers: 'PMS Integrations, Authorization Logs, RADIUS Report, PMS Activity Logs · Configure: PMS settings and sync', actions: ['view', 'configure'] },
            { key: 'reports',    label: 'Reports', icon: 'fa-chart-bar', covers: 'Reports', actions: ['view'] },
            { key: 'audit_logs', label: 'Logs', icon: 'fa-history', covers: 'Hotspot Logs, Login & Activity', actions: ['view', 'export'] },
        ],
    },
    {
        label: 'Finance',
        modules: [
            { key: 'billing',  label: 'Billing & Payment Gateways', icon: 'fa-credit-card', covers: 'Billing, Payment Gateways', actions: CRUD },
            { key: 'webhooks', label: 'Webhooks', icon: 'fa-satellite-dish', covers: 'Webhooks · Configure: send a test', actions: [...CRUD, 'configure'] },
        ],
    },
];
const MODULE_LIST = MODULE_GROUPS.flatMap((g) => g.modules);
const MODULE_BY_KEY = Object.fromEntries(MODULE_LIST.map((m) => [m.key, m]));

// The module whose View a page needs to show its data — for the hint next to a page
// left in the sidebar of a role that may not view it.
const PAGE_NEEDS = {
    '/branches': 'branches', '/admins': 'admins', '/users': 'users',
    '/rooms': 'settings', '/branch-pages': 'settings', '/user-types': 'settings', '/vlan-profiles': 'settings',
    '/nas': 'routers', '/devices': 'devices', '/device-status': 'devices',
    '/plans': 'plans', '/plan-templates': 'plans', '/addons': 'plans', '/membership-tiers': 'plans', '/policy-rules': 'plans', '/scheduler-jobs': 'plans',
    '/vouchers': 'vouchers', '/media': 'media', '/templates': 'templates', '/campaigns': 'campaigns',
    '/sessions': 'sessions', '/session-report': 'sessions', '/connected-users': 'sessions', '/device-auth': 'sessions', '/upgrade-approvals': 'sessions',
    '/reports': 'reports', '/traffic': 'dashboard',
    '/integrations': 'pms', '/auth-logs': 'pms', '/radius-report': 'pms', '/pms-logs': 'pms',
    '/logs': 'audit_logs', '/guest-activity': 'audit_logs', '/activity-log': 'audit_logs',
    '/payment-config': 'billing', '/webhooks': 'webhooks', '/auth-sets': 'auth_sets',
};

const menuRoleOf = (t) => (t?.isCustom ? t.baseRole : t?.role);
const roleLabel = (t) => t?.label || BASE[t?.role]?.label || t?.role || '';

/** A role's hidden pages as page paths — a module saved before pages (all its pages) included. */
function hiddenPagesOf(t) {
    const pages = pagesOf(menuRoleOf(t)).map((p) => p.path);
    const out = new Set();
    for (const entry of t?.sidebarHidden || []) {
        if (entry.startsWith('/')) out.add(entry);
        else pages.filter((p) => PATH_TO_MODULE[p] === entry).forEach((p) => out.add(p));
    }
    out.delete('/dashboard');
    return [...out].sort();
}

/** The editable copy of a role. Modules with nothing allowed are left out, so copies compare. */
function draftOf(t) {
    const perms = {};
    for (const m of t?.modules || []) {
        const actions = [...new Set(m.actions || [])].sort();
        if (actions.length) perms[m.module] = actions;
    }
    return { label: roleLabel(t), isEnabled: t?.isEnabled !== false, perms, hidden: hiddenPagesOf(t) };
}

/** A role's pages, section by section, as its sidebar shows them. */
function sectionsOf(menuRole) {
    const sections = [];
    for (const page of pagesOf(menuRole)) {
        const last = sections[sections.length - 1];
        if (last && last.label === page.section) last.pages.push(page);
        else sections.push({ label: page.section, pages: [page] });
    }
    return sections;
}

const sortRoles = (list) => [
    ...BASE_ROLES.map((b) => list.find((t) => t.role === b.key)).filter(Boolean),
    ...list.filter((t) => t.isCustom).sort((a, b) => roleLabel(a).localeCompare(roleLabel(b))),
];

const errorText = (err, fallback) => err?.response?.data?.message || fallback;

// ─── A small dialog: focus moves in, Escape closes ───────────────────────────────
const Dialog = ({ title, titleId, onClose, children, footer, wide = false }) => {
    const ref = useRef(null);
    const closeRef = useRef(onClose);
    closeRef.current = onClose;
    useEffect(() => {
        const previous = document.activeElement;
        ref.current?.querySelector('input, select, button:not(.ac-dialog-close)')?.focus();
        const onKey = (e) => { if (e.key === 'Escape') closeRef.current(); };
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('keydown', onKey); previous?.focus?.(); };
    }, []);
    return (
        <div className="ac-dialog-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div ref={ref} className={`ac-dialog${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
                <div className="ac-dialog-head">
                    <h2 id={titleId}>{title}</h2>
                    <button type="button" className="ac-dialog-close" onClick={onClose} aria-label="Close">
                        <i className="fas fa-times"></i>
                    </button>
                </div>
                <div className="ac-dialog-body">{children}</div>
                {footer && <div className="ac-dialog-foot">{footer}</div>}
            </div>
        </div>
    );
};

const AccessControl = () => {
    const [view, setView]         = useState('roles');      // roles | admins
    const [loading, setLoading]   = useState(true);
    const [roles, setRoles]       = useState([]);           // as saved
    const [drafts, setDrafts]     = useState({});           // role → editable copy
    const [activeRole, setActiveRole] = useState('tenant_admin');
    const [section, setSection]   = useState('perms');      // perms | sidebar
    const [saving, setSaving]     = useState(false);
    const [newRole, setNewRole]   = useState(null);         // { label, baseRole } while the dialog is open
    const [confirm, setConfirm]   = useState(null);         // { title, body, confirmLabel, onConfirm }

    const fetchRoles = useCallback(async () => {
        try {
            setLoading(true);
            const res = await accessControlAPI.getAll();
            const list = sortRoles(Array.isArray(res.data?.data) ? res.data.data : []);
            setRoles(list);
            setDrafts(Object.fromEntries(list.map((t) => [t.role, draftOf(t)])));
        } catch {
            toast.error('Failed to load access control');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchRoles(); }, [fetchRoles]);

    const saved = roles.find((t) => t.role === activeRole) || roles[0];
    const role = saved?.role;
    const draft = drafts[role];
    const baseline = useMemo(() => draftOf(saved), [saved]);
    const isDirty = (t) => !!drafts[t.role] && JSON.stringify(drafts[t.role]) !== JSON.stringify(draftOf(t));
    const dirty = !!saved && isDirty(saved);

    const updateDraft = (fn) => setDrafts((prev) => ({ ...prev, [role]: fn(prev[role]) }));

    // ── What it may do ───────────────────────────────────────────────────────────
    const allowed = (mod, action) => !!draft?.perms[mod]?.includes(action);

    // View is what opens a page: giving any other action gives View; taking View takes the rest.
    const setActions = (d, mod, on, actions) => {
        const cur = new Set(d.perms[mod] || []);
        const applicable = MODULE_BY_KEY[mod]?.actions || [];
        for (const a of actions) {
            if (on) cur.add(a); else cur.delete(a);
        }
        if (on && applicable.includes('view')) cur.add('view');
        if (!on && actions.includes('view')) applicable.forEach((a) => cur.delete(a));
        const perms = { ...d.perms };
        if (cur.size) perms[mod] = [...cur].sort(); else delete perms[mod];
        return { ...d, perms };
    };
    const toggleAction = (mod, action) => updateDraft((d) => setActions(d, mod, !allowed(mod, action), [action]));
    const toggleModule = (m) => {
        const all = m.actions.every((a) => allowed(m.key, a));
        updateDraft((d) => setActions(d, m.key, !all, m.actions));
    };
    const setEverything = (on) => updateDraft((d) => MODULE_LIST.reduce((acc, m) => setActions(acc, m.key, on, m.actions), d));

    // ── Sidebar menu ─────────────────────────────────────────────────────────────
    const togglePage = (path) => updateDraft((d) => ({
        ...d,
        hidden: d.hidden.includes(path) ? d.hidden.filter((p) => p !== path) : [...d.hidden, path].sort(),
    }));
    const setAllPages = (hide) => updateDraft((d) => ({
        ...d,
        hidden: hide ? pagesOf(menuRoleOf(saved)).map((p) => p.path).filter((p) => p !== '/dashboard').sort() : [],
    }));

    const handleSave = async () => {
        if (!saved || !draft) return;
        if (saved.isCustom && !draft.label.trim()) { toast.error('Give the role a name'); return; }
        setSaving(true);
        try {
            const res = await accessControlAPI.update(role, {
                isEnabled: draft.isEnabled,
                modules: Object.entries(draft.perms).map(([module, actions]) => ({ module, actions })),
                sidebarHidden: draft.hidden,
                ...(saved.isCustom ? { label: draft.label.trim() } : {}),
            });
            const next = { ...saved, ...res.data.data, label: res.data.data?.label || saved.label, adminCount: saved.adminCount };
            setRoles((prev) => prev.map((t) => (t.role === role ? next : t)));
            setDrafts((prev) => ({ ...prev, [role]: draftOf(next) }));
            toast.success(`${roleLabel(next)} saved`);
        } catch (err) {
            toast.error(errorText(err, 'Failed to save'));
        } finally {
            setSaving(false);
        }
    };

    const createRole = async (e) => {
        e.preventDefault();
        if (!newRole.label.trim()) return;
        try {
            const res = await accessControlAPI.createRole({ label: newRole.label.trim(), baseRole: newRole.baseRole });
            const created = { ...res.data.data, adminCount: 0 };
            setRoles((prev) => sortRoles([...prev, created]));
            setDrafts((prev) => ({ ...prev, [created.role]: draftOf(created) }));
            setActiveRole(created.role);
            setSection('perms');
            setNewRole(null);
            toast.success(`Role "${roleLabel(created)}" created — set what it may do, then save`);
        } catch (err) {
            toast.error(errorText(err, 'Failed to create the role'));
        }
    };

    const removeRole = async (force = false) => {
        const name = roleLabel(saved);
        try {
            await accessControlAPI.deleteRole(role, force);
            setRoles((prev) => prev.filter((t) => t.role !== role));
            setDrafts((prev) => { const next = { ...prev }; delete next[role]; return next; });
            setActiveRole('tenant_admin');
            setConfirm(null);
            toast.success(`Role "${name}" deleted`);
        } catch (err) {
            if (err?.response?.status === 409) {
                setConfirm({
                    title: `Delete "${name}"?`,
                    body: err.response.data.message,
                    confirmLabel: 'Delete anyway',
                    onConfirm: () => removeRole(true),
                });
                return;
            }
            toast.error(errorText(err, 'Failed to delete the role'));
        }
    };

    const askRemoveRole = () => setConfirm({
        title: `Delete "${roleLabel(saved)}"?`,
        body: 'The role and its settings are deleted. This cannot be undone.',
        confirmLabel: 'Delete role',
        onConfirm: () => removeRole(false),
    });

    // ── Administrators ───────────────────────────────────────────────────────────
    const [admins, setAdmins]         = useState([]);
    const [adminsLoading, setAdminsLoading] = useState(false);
    const [search, setSearch]         = useState('');
    const [editing, setEditing]       = useState(null);   // { admin, modes: { path: default|show|hide } }

    useEffect(() => {
        if (view !== 'admins') return undefined;
        const t = setTimeout(async () => {
            try {
                setAdminsLoading(true);
                const res = await accessControlAPI.listUsers(search.trim());
                setAdmins(Array.isArray(res.data?.data) ? res.data.data : []);
            } catch (err) {
                toast.error(errorText(err, 'Failed to load administrators'));
            } finally {
                setAdminsLoading(false);
            }
        }, search ? 300 : 0);
        return () => clearTimeout(t);
    }, [view, search]);

    const customRolesFor = (baseRole) => roles.filter((t) => t.isCustom && t.baseRole === baseRole);
    // The role an administrator works under, as saved: their custom role's, else their built-in role's.
    const templateFor = (a) => {
        const custom = a.customRole && roles.find((t) => t.role === a.customRole && t.baseRole === a.role);
        return custom || roles.find((t) => t.role === a.role);
    };

    const assignRole = async (a, customRole) => {
        try {
            const res = await accessControlAPI.updateUser(a._id, { customRole });
            const now = res.data.data?.customRole || null;
            setAdmins((prev) => prev.map((x) => (x._id === a._id ? { ...x, customRole: now || undefined } : x)));
            setRoles((prev) => prev.map((t) => {
                if (!t.isCustom) return t;
                const delta = (t.role === now ? 1 : 0) - (t.role === a.customRole ? 1 : 0);
                return delta ? { ...t, adminCount: Math.max(0, (t.adminCount || 0) + delta) } : t;
            }));
            const t = now ? roles.find((r) => r.role === now) : roles.find((r) => r.role === a.role);
            toast.success(`${a.name} now works as ${roleLabel(t)}`);
        } catch (err) {
            toast.error(errorText(err, 'Failed to change the role'));
        }
    };

    const openSidebarFor = (a) => {
        const modes = {};
        for (const p of a.accessOverrides?.hiddenPages || []) modes[p] = 'hide';
        for (const p of a.accessOverrides?.shownPages || []) modes[p] = 'show';
        setEditing({ admin: a, modes });
    };
    const setMode = (path, mode) => setEditing((e) => ({ ...e, modes: { ...e.modes, [path]: mode } }));

    const saveAdminSidebar = async () => {
        const { admin: a, modes } = editing;
        const hiddenPages = Object.keys(modes).filter((p) => modes[p] === 'hide');
        const shownPages = Object.keys(modes).filter((p) => modes[p] === 'show');
        try {
            const res = await accessControlAPI.updateUser(a._id, { hiddenPages, shownPages });
            const overrides = res.data.data?.accessOverrides || { hiddenPages, shownPages };
            setAdmins((prev) => prev.map((x) => (x._id === a._id ? { ...x, accessOverrides: overrides } : x)));
            setEditing(null);
            toast.success(`Sidebar for ${a.name} saved`);
        } catch (err) {
            toast.error(errorText(err, 'Failed to save the sidebar'));
        }
    };

    // Counts only pages of their menu (one left from a former role changes nothing).
    const overridesSummary = (a) => {
        const menu = new Set(pagesOf(a.role).map((p) => p.path));
        const hidden = (a.accessOverrides?.hiddenPages || []).filter((p) => menu.has(p)).length;
        const shown = (a.accessOverrides?.shownPages || []).filter((p) => menu.has(p)).length;
        if (!hidden && !shown) return 'As the role';
        return [hidden && `${hidden} hidden`, shown && `${shown} always shown`].filter(Boolean).join(' · ');
    };

    if (loading) {
        return (
            <div className="ac-loading">
                <i className="fas fa-spinner fa-spin"></i>
                <span>Loading access control…</span>
            </div>
        );
    }

    const menuRole = menuRoleOf(saved);
    const sections = saved ? sectionsOf(menuRole) : [];
    const hiddenCount = draft?.hidden.length || 0;

    return (
        <div className="ac-page">
            <div className="ac-header">
                <div>
                    <h1>Access Control</h1>
                    <p>The roles, what each may do, and which sidebar pages each administrator sees.</p>
                </div>
            </div>

            <div className="ac-views" role="tablist" aria-label="Access Control">
                <button type="button" role="tab" aria-selected={view === 'roles'} className={view === 'roles' ? 'active' : ''} onClick={() => setView('roles')}>
                    <i className="fas fa-user-tag"></i> Roles
                </button>
                <button type="button" role="tab" aria-selected={view === 'admins'} className={view === 'admins' ? 'active' : ''} onClick={() => setView('admins')}>
                    <i className="fas fa-users-cog"></i> Administrators
                </button>
            </div>

            {view === 'roles' && saved && draft && (
                <>
                    <div className="ac-rolebar" aria-label="Roles">
                        {roles.map((t) => {
                            const d = drafts[t.role];
                            return (
                                <button
                                    key={t.role}
                                    type="button"
                                    className={`ac-role${t.role === role ? ' active' : ''}`}
                                    aria-pressed={t.role === role}
                                    onClick={() => setActiveRole(t.role)}
                                >
                                    <i className={`fas ${t.isCustom ? 'fa-user-tag' : BASE[t.role]?.icon}`}></i>
                                    <span className="ac-role-text">
                                        <span className="ac-role-name">{d?.label || roleLabel(t)}</span>
                                        <span className="ac-role-sub">
                                            {t.isCustom ? `Based on ${BASE[t.baseRole]?.label} · ${t.adminCount || 0} admin${t.adminCount === 1 ? '' : 's'}` : 'Built-in'}
                                        </span>
                                    </span>
                                    {d && !d.isEnabled && <span className="ac-status off">Off</span>}
                                    {isDirty(t) && <span className="ac-unsaved" title="Unsaved changes" aria-label="Unsaved changes"></span>}
                                </button>
                            );
                        })}
                        <button type="button" className="ac-role ac-role-new" onClick={() => setNewRole({ label: '', baseRole: 'branch_admin' })}>
                            <i className="fas fa-plus"></i>
                            <span className="ac-role-text"><span className="ac-role-name">New role</span></span>
                        </button>
                    </div>

                    <section className="ac-panel" aria-labelledby="ac-role-title">
                        <div className="ac-panel-head">
                            <div className="ac-panel-title">
                                {saved.isCustom ? (
                                    <label className="ac-rename">
                                        <span className="ac-sr">Role name</span>
                                        <input
                                            id="ac-role-title"
                                            value={draft.label}
                                            size={Math.max(8, draft.label.length + 2)}
                                            maxLength={60}
                                            onChange={(e) => updateDraft((d) => ({ ...d, label: e.target.value }))}
                                        />
                                        <i className="fas fa-pen" aria-hidden="true"></i>
                                    </label>
                                ) : (
                                    <h2 id="ac-role-title">{draft.label}</h2>
                                )}
                                <p>
                                    {saved.isCustom
                                        ? <>Based on <strong>{BASE[saved.baseRole]?.label}</strong> — its administrators reach {BASE[saved.baseRole]?.scope}. Give it to them under Administrators.</>
                                        : <>Built-in role — reaches {BASE[role]?.scope}. A role you make can replace it for some of its administrators.</>}
                                </p>
                            </div>
                            <div className="ac-panel-actions">
                                <label className="ac-switch">
                                    <input
                                        type="checkbox"
                                        checked={draft.isEnabled}
                                        onChange={() => updateDraft((d) => ({ ...d, isEnabled: !d.isEnabled }))}
                                    />
                                    <span className="ac-switch-track" aria-hidden="true"></span>
                                    <span>Role on</span>
                                </label>
                                {saved.isCustom && (
                                    <button type="button" className="ac-btn ac-btn-danger" onClick={askRemoveRole}>
                                        <i className="fas fa-trash-alt"></i> Delete role
                                    </button>
                                )}
                                {dirty && (
                                    <button type="button" className="ac-btn ac-btn-ghost" onClick={() => setDrafts((prev) => ({ ...prev, [role]: baseline }))}>
                                        Discard
                                    </button>
                                )}
                                <button type="button" className="ac-btn ac-btn-primary" onClick={handleSave} disabled={!dirty || saving}>
                                    {saving ? <><i className="fas fa-spinner fa-spin"></i> Saving…</> : <><i className="fas fa-save"></i> Save</>}
                                </button>
                            </div>
                        </div>

                        {!draft.isEnabled && (
                            <div className="ac-banner off" role="status">
                                <i className="fas fa-ban"></i>
                                Switched off: its administrators cannot sign in, and anyone signed in is signed out at their next click.
                                {!saved.isCustom && customRolesFor(role).length > 0 && ' Those working as a role based on it follow that role.'}
                            </div>
                        )}

                        <div className="ac-sections" role="tablist" aria-label="Role settings">
                            <button type="button" role="tab" aria-selected={section === 'perms'} className={section === 'perms' ? 'active' : ''} onClick={() => setSection('perms')}>
                                What it may do
                            </button>
                            <button type="button" role="tab" aria-selected={section === 'sidebar'} className={section === 'sidebar' ? 'active' : ''} onClick={() => setSection('sidebar')}>
                                Sidebar menu{hiddenCount > 0 && <span className="ac-count">{hiddenCount} hidden</span>}
                            </button>
                        </div>

                        {section === 'perms' ? (
                            <>
                                <div className="ac-tools">
                                    <button type="button" className="ac-btn ac-btn-ghost sm" onClick={() => setEverything(true)}>Allow everything</button>
                                    <button type="button" className="ac-btn ac-btn-ghost sm" onClick={() => setEverything(false)}>Clear everything</button>
                                    <span className="ac-note">The API checks these on every request. View is needed for the rest; a dash means nothing checks that action there.</span>
                                </div>
                                <div className="ac-table-wrap">
                                    <table className="ac-matrix">
                                        <thead>
                                            <tr>
                                                <th scope="col" className="ac-col-module">Module</th>
                                                {ACTIONS.map((a) => <th key={a.key} scope="col" title={a.hint}>{a.label}</th>)}
                                                <th scope="col">All</th>
                                            </tr>
                                        </thead>
                                        {MODULE_GROUPS.map((g) => (
                                            <tbody key={g.label}>
                                                <tr className="ac-group"><th colSpan={ACTIONS.length + 2} scope="colgroup">{g.label}</th></tr>
                                                {g.modules.map((m) => {
                                                    const all = m.actions.every((a) => allowed(m.key, a));
                                                    const some = m.actions.some((a) => allowed(m.key, a));
                                                    return (
                                                        <tr key={m.key} className={some ? 'on' : ''}>
                                                            <th scope="row" className="ac-col-module">
                                                                <i className={`fas ${m.icon}`} aria-hidden="true"></i>
                                                                <span>
                                                                    <span className="ac-module-name">{m.label}</span>
                                                                    <span className="ac-module-covers">{m.covers}</span>
                                                                </span>
                                                            </th>
                                                            {ACTIONS.map((a) => (
                                                                <td key={a.key}>
                                                                    {m.actions.includes(a.key) ? (
                                                                        <input
                                                                            type="checkbox"
                                                                            className="ac-check"
                                                                            checked={allowed(m.key, a.key)}
                                                                            onChange={() => toggleAction(m.key, a.key)}
                                                                            aria-label={`${m.label}: ${a.label}`}
                                                                        />
                                                                    ) : <span className="ac-na" title="Nothing checks this here">—</span>}
                                                                </td>
                                                            ))}
                                                            <td>
                                                                <input
                                                                    type="checkbox"
                                                                    className="ac-check"
                                                                    checked={all}
                                                                    ref={(el) => { if (el) el.indeterminate = some && !all; }}
                                                                    onChange={() => toggleModule(m)}
                                                                    aria-label={`${m.label}: all`}
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        ))}
                                    </table>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="ac-tools">
                                    <button type="button" className="ac-btn ac-btn-ghost sm" onClick={() => setAllPages(false)}>Show every page</button>
                                    <button type="button" className="ac-btn ac-btn-ghost sm" onClick={() => setAllPages(true)}>Hide every page</button>
                                    <span className="ac-note">
                                        {saved.isCustom ? `The ${BASE[menuRole]?.label} menu. ` : ''}
                                        A hidden page is gone from the sidebar and closed by its address too. The Dashboard always stays.
                                    </span>
                                </div>
                                <div className="ac-pages-wrap">
                                    {sections.map((sec) => (
                                        <fieldset key={sec.label || 'main'} className="ac-pages-section">
                                            <legend>{sec.label || 'Main'}</legend>
                                            <ul className="ac-pages">
                                                {sec.pages.map((p) => {
                                                    const hidden = draft.hidden.includes(p.path);
                                                    const locked = p.path === '/dashboard';
                                                    const needs = PAGE_NEEDS[p.path];
                                                    const blind = !hidden && needs && !allowed(needs, 'view');
                                                    return (
                                                        <li key={p.path} className={hidden ? 'hidden' : ''}>
                                                            <i className={`fas ${p.icon} ac-page-icon`} aria-hidden="true"></i>
                                                            <span className="ac-page-name">
                                                                {p.label}
                                                                {blind && (
                                                                    <span className="ac-page-warn" title="The page opens but shows nothing — give View under What it may do">
                                                                        <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
                                                                        Needs View on {MODULE_BY_KEY[needs]?.label}
                                                                    </span>
                                                                )}
                                                            </span>
                                                            <label className="ac-switch">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={!hidden}
                                                                    disabled={locked}
                                                                    onChange={() => togglePage(p.path)}
                                                                    aria-label={`${p.label} in the sidebar`}
                                                                />
                                                                <span className="ac-switch-track" aria-hidden="true"></span>
                                                                <span className="ac-switch-label">{locked ? 'Always' : hidden ? 'Hidden' : 'Shown'}</span>
                                                            </label>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </fieldset>
                                    ))}
                                </div>
                            </>
                        )}
                    </section>
                </>
            )}

            {view === 'admins' && (
                <section className="ac-panel" aria-label="Administrators">
                    <div className="ac-tools ac-tools-top">
                        <label className="ac-search">
                            <i className="fas fa-search" aria-hidden="true"></i>
                            <span className="ac-sr">Search administrators</span>
                            <input
                                type="search"
                                placeholder="Search name, email or username"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </label>
                        <span className="ac-note">
                            "Works as" gives an administrator a role you made, based on their built-in role — its permissions and sidebar replace the built-in role's; their data reach stays the same. Their own sidebar comes on top.
                        </span>
                    </div>
                    <div className="ac-table-wrap">
                        <table className="ac-admins">
                            <thead>
                                <tr>
                                    <th scope="col">Administrator</th>
                                    <th scope="col">Organization</th>
                                    <th scope="col">Built-in role</th>
                                    <th scope="col">Works as</th>
                                    <th scope="col">Own sidebar</th>
                                    <th scope="col"><span className="ac-sr">Actions</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {admins.map((a) => {
                                    const options = customRolesFor(a.role);
                                    return (
                                        <tr key={a._id}>
                                            <td>
                                                <span className="ac-admin-name">{a.name}</span>
                                                <span className="ac-admin-email">{a.email}</span>
                                                {a.status && a.status !== 'active' && <span className="ac-status off">{a.status}</span>}
                                            </td>
                                            <td>
                                                {a.tenantId?.name || '—'}
                                                {a.branchId?.name && <span className="ac-admin-email">{a.branchId.name}</span>}
                                            </td>
                                            <td>{BASE[a.role]?.label || a.role}</td>
                                            <td>
                                                <select
                                                    className="ac-select"
                                                    value={a.customRole && options.some((t) => t.role === a.customRole) ? a.customRole : ''}
                                                    onChange={(e) => assignRole(a, e.target.value)}
                                                    aria-label={`Role for ${a.name}`}
                                                    disabled={!options.length && !a.customRole}
                                                    title={!options.length ? `Make a role based on ${BASE[a.role]?.label} under Roles to choose it here` : undefined}
                                                >
                                                    <option value="">{BASE[a.role]?.label || a.role} (built-in)</option>
                                                    {options.map((t) => (
                                                        <option key={t.role} value={t.role}>{roleLabel(t)}{t.isEnabled === false ? ' (off)' : ''}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className={overridesSummary(a) === 'As the role' ? 'ac-muted' : 'ac-own'}>
                                                {overridesSummary(a)}
                                            </td>
                                            <td className="ac-row-actions">
                                                <button type="button" className="ac-btn ac-btn-ghost sm" onClick={() => openSidebarFor(a)} aria-label={`Sidebar for ${a.name}`}>
                                                    <i className="fas fa-bars"></i> Sidebar
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {!admins.length && (
                                    <tr>
                                        <td colSpan={6} className="ac-empty">
                                            {adminsLoading ? 'Loading…' : search ? 'No administrator matches the search.' : 'No administrators yet.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {newRole && (
                <Dialog
                    title="New role"
                    titleId="ac-new-role-title"
                    onClose={() => setNewRole(null)}
                    footer={(
                        <>
                            <button type="button" className="ac-btn ac-btn-ghost" onClick={() => setNewRole(null)}>Cancel</button>
                            <button type="submit" form="ac-new-role" className="ac-btn ac-btn-primary" disabled={!newRole.label.trim()}>
                                <i className="fas fa-plus"></i> Create role
                            </button>
                        </>
                    )}
                >
                    <form id="ac-new-role" className="ac-form" onSubmit={createRole}>
                        <label>
                            <span>Name</span>
                            <input
                                value={newRole.label}
                                maxLength={60}
                                placeholder="e.g. Front Desk"
                                onChange={(e) => setNewRole((r) => ({ ...r, label: e.target.value }))}
                                required
                            />
                        </label>
                        <label>
                            <span>Based on</span>
                            <select className="ac-select" value={newRole.baseRole} onChange={(e) => setNewRole((r) => ({ ...r, baseRole: e.target.value }))}>
                                {BASE_ROLES.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
                            </select>
                        </label>
                        <p className="ac-note">
                            Like {BASE[newRole.baseRole]?.label}, its administrators reach {BASE[newRole.baseRole]?.scope} and
                            see the {BASE[newRole.baseRole]?.label} menu. It starts with what {BASE[newRole.baseRole]?.label} may
                            do and sees — change that after creating it.
                        </p>
                    </form>
                </Dialog>
            )}

            {editing && (() => {
                const a = editing.admin;
                const tpl = templateFor(a);
                const roleHidden = new Set(hiddenPagesOf(tpl));
                return (
                    <Dialog
                        wide
                        title={`Sidebar for ${a.name}`}
                        titleId="ac-admin-sidebar-title"
                        onClose={() => setEditing(null)}
                        footer={(
                            <>
                                <button type="button" className="ac-btn ac-btn-ghost" onClick={() => setEditing((e) => ({ ...e, modes: {} }))}>All as the role</button>
                                <span className="ac-foot-gap"></span>
                                <button type="button" className="ac-btn ac-btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
                                <button type="button" className="ac-btn ac-btn-primary" onClick={saveAdminSidebar}>
                                    <i className="fas fa-save"></i> Save
                                </button>
                            </>
                        )}
                    >
                        <p className="ac-note ac-dialog-lead">
                            Works as <strong>{roleLabel(tpl)}</strong>. "As the role" follows that role's sidebar; Show and Hide apply to {a.name} only.
                        </p>
                        {sectionsOf(a.role).map((sec) => (
                            <fieldset key={sec.label || 'main'} className="ac-pages-section">
                                <legend>{sec.label || 'Main'}</legend>
                                <ul className="ac-pages">
                                    {sec.pages.map((p) => {
                                        const mode = editing.modes[p.path] || 'default';
                                        const locked = p.path === '/dashboard';
                                        const effectiveHidden = !locked && (mode === 'hide' || (mode === 'default' && roleHidden.has(p.path)));
                                        return (
                                            <li key={p.path} className={effectiveHidden ? 'hidden' : ''}>
                                                <i className={`fas ${p.icon} ac-page-icon`} aria-hidden="true"></i>
                                                <span className="ac-page-name">{p.label}</span>
                                                {locked ? (
                                                    <span className="ac-switch-label">Always</span>
                                                ) : (
                                                    <div className="ac-seg" role="radiogroup" aria-label={p.label}>
                                                        {[
                                                            ['default', `As the role (${roleHidden.has(p.path) ? 'hidden' : 'shown'})`],
                                                            ['show', 'Show'],
                                                            ['hide', 'Hide'],
                                                        ].map(([value, text]) => (
                                                            <label key={value} className={mode === value ? 'active' : ''}>
                                                                <input
                                                                    type="radio"
                                                                    name={`ac-mode-${p.path}`}
                                                                    checked={mode === value}
                                                                    onChange={() => setMode(p.path, value)}
                                                                />
                                                                {text}
                                                            </label>
                                                        ))}
                                                    </div>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </fieldset>
                        ))}
                    </Dialog>
                );
            })()}

            {confirm && (
                <Dialog
                    title={confirm.title}
                    titleId="ac-confirm-title"
                    onClose={() => setConfirm(null)}
                    footer={(
                        <>
                            <button type="button" className="ac-btn ac-btn-ghost" onClick={() => setConfirm(null)}>Cancel</button>
                            <button type="button" className="ac-btn ac-btn-danger solid" onClick={confirm.onConfirm}>{confirm.confirmLabel}</button>
                        </>
                    )}
                >
                    <p className="ac-note">{confirm.body}</p>
                </Dialog>
            )}
        </div>
    );
};

export default AccessControl;
